/**
 * Map a shop-indent (citizen redeem order) into Cart Model Bill form state
 * and the same POST /api/smartbills/bills payload CartModelBill uses.
 * No new tables — reuses the bill path that already appears in View Bills.
 */

import { gstAmountFromPercent } from './billTaxes';

const phone10 = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  return d.length >= 10 ? d.slice(-10) : d;
};

const money = (n) => Math.max(0, Math.round((Number(n) || 0) * 100) / 100);

export function couponSavingsFromIndent(indent = {}) {
  const deductions = Array.isArray(indent.deductions) ? indent.deductions : [];
  const fromDed = deductions.reduce((s, d) => s + (Number(d?.amount) || 0), 0);
  if (fromDed > 0) return money(fromDed);
  const sub = Number(indent.subtotalInr || 0);
  const finalAmt =
    indent.finalAmountInr != null ? Number(indent.finalAmountInr) : null;
  if (finalAmt != null && sub > finalAmt) return money(sub - finalAmt);
  // Citizen note often has: "list est. ₹80 · after coupon ₹60"
  const note = String(indent.note || '');
  const listM = note.match(/list est\.?\s*₹?\s*(\d+(?:\.\d+)?)/i);
  const afterM = note.match(/after coupon\s*₹?\s*(\d+(?:\.\d+)?)/i);
  if (listM && afterM) {
    const listEst = Number(listM[1]) || 0;
    const after = Number(afterM[1]) || 0;
    if (listEst > after) return money(listEst - after);
  }
  return 0;
}

export const SHOP_INDENT_CART_PREFILL_KEY = 'locsy_shop_indent_cart_prefill_v1';

/** In-memory copy survives React Strict Mode remount (sessionStorage take+delete does not). */
let memoryShopIndentCartPrefill = null;

/** Persist prefill so Cart Bill survives route remount (location.state alone is flaky). */
export function stashShopIndentCartPrefill(prefill) {
  memoryShopIndentCartPrefill = prefill || null;
  try {
    sessionStorage.setItem(SHOP_INDENT_CART_PREFILL_KEY, JSON.stringify(prefill));
  } catch {
    /* ignore quota */
  }
}

/** Read prefill without deleting — safe for Strict Mode double-mount. */
export function takeShopIndentCartPrefill() {
  if (memoryShopIndentCartPrefill && typeof memoryShopIndentCartPrefill === 'object') {
    return memoryShopIndentCartPrefill;
  }
  try {
    const raw = sessionStorage.getItem(SHOP_INDENT_CART_PREFILL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    memoryShopIndentCartPrefill = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export function clearShopIndentCartPrefill() {
  memoryShopIndentCartPrefill = null;
  try {
    sessionStorage.removeItem(SHOP_INDENT_CART_PREFILL_KEY);
  } catch {
    /* ignore */
  }
}

/** Cart Model Bill React state prefill (location.state). */
export function shopIndentToCartBillPrefill(indent = {}) {
  const items = Array.isArray(indent.items) ? indent.items : [];
  const deductions = Array.isArray(indent.deductions) ? indent.deductions : [];
  const couponOff = couponSavingsFromIndent(indent);
  const payable =
    indent.finalAmountInr != null
      ? money(indent.finalAmountInr)
      : money(
          items.reduce(
            (s, it) =>
              s +
              (Number(it.quantity) || 1) * (Number(it.unitPrice ?? it.price) || 0),
            0,
          ) - couponOff,
        );
  const token = String(indent.tokenNo || '').trim();
  const notes = [
    token ? `Shop order token ${token}` : 'Shop order',
    `indent:${indent.id}`,
    indent.ownerNote || indent.note || '',
  ]
    .filter(Boolean)
    .join(' · ');

  // If items empty, try product name from coupon / note (offer product).
  let lineItems = items;
  if (!lineItems.length) {
    const fromCoupon =
      deductions.find((d) => d?.couponCode || d?.label)?.label ||
      indent.couponCode ||
      '';
    const nameFromNote = String(indent.note || '').match(
      /Redeem coupon\s+\S+\s*\(([^)]+)\)/i,
    );
    const productName =
      (nameFromNote && nameFromNote[1]) ||
      String(fromCoupon).replace(/^Coupon\s+/i, '') ||
      'Offer product';
    const listEst =
      Number(
        (String(indent.note || '').match(/list est\.?\s*₹?\s*(\d+)/i) || [])[1],
      ) ||
      Number(indent.subtotalInr) ||
      payable + couponOff;
    lineItems = [
      {
        name: productName.trim() || 'Offer product',
        quantity: 1,
        unitPrice: listEst > 0 ? listEst : payable + couponOff,
      },
    ];
  }

  const cartItems = lineItems.map((it, idx) => ({
    key: `indent-${indent.id || 'x'}-${idx}-${Date.now()}`,
    serviceId: it.offeringId || it.serviceId || null,
    name: String(it.name || 'Offer product'),
    unitPrice: money(it.unitPrice ?? it.price ?? 0),
    quantity: Math.max(1, Number(it.quantity) || 1),
    challana: 0,
    gstPercent: Number(it.gstPercent ?? it.gst_percent ?? 0) || 0,
    mainCategory: 'Shop order',
    subCategory: indent.couponCode ? `Coupon ${indent.couponCode}` : 'Redeem',
    imageUrl: it.imageUrl || null,
  }));

  const couponLabel =
    indent.couponCode ||
    deductions.find((d) => d?.couponCode)?.couponCode ||
    (String(indent.note || '').match(/Redeem coupon\s+([A-Za-z0-9_-]+)/i) ||
      [])[1] ||
    '';

  const appliedCouponItems =
    couponOff > 0
      ? [
          {
            // Client row key only — must NOT be sent as bill_offers.offer_item_id (FK).
            id: `indent-coupon-${indent.couponId || indent.id || Date.now()}`,
            offerItemId: null,
            offerId: null,
            name: couponLabel
              ? `Coupon ${couponLabel}`
              : 'Redeem coupon',
            quantity: 1,
            actualPrice: couponOff,
            offerPrice: 0,
            actualTotal: couponOff,
            afterOfferTotal: 0,
            totalSavings: couponOff,
            offerApplied: true,
            offerType: 'allotted_retrieve',
            claimedCouponId: indent.couponId || null,
            type: 'allotted_coupon',
            code: couponLabel,
            service: cartItems[0]?.name || '',
          },
        ]
      : [];

  return {
    shopIndentId: indent.id,
    customer: {
      name: String(indent.customerName || 'Customer'),
      phone: phone10(indent.customerPhone),
      profession: '',
      email: '',
      address: '',
      city: '',
      pincode: '',
      notes,
    },
    cartItems,
    payment: {
      method: 'upi',
      status: 'paid',
      cash: 0,
      upi: payable,
      card: 0,
      discount: 0,
    },
    appliedCouponItems,
  };
}

/** Same shape as CartModelBill.buildPayload — list prices + couponBillItems; server deducts. */
export function shopIndentToSmartBillPayload(indent = {}) {
  const prefill = shopIndentToCartBillPrefill(indent);
  const couponOff = couponSavingsFromIndent(indent);
  const selectedServices = prefill.cartItems.map((item) => {
    const quantity = Number(item.quantity) || 1;
    const serviceCharge = Number(item.unitPrice) || 0;
    return {
      id: item.serviceId || null,
      name: item.name,
      displayName: item.name,
      quantity,
      price: serviceCharge,
      serviceCharge,
      challanaAmount: 0,
      gstAmount: gstAmountFromPercent(serviceCharge, item.gstPercent),
      gstPercent: Number(item.gstPercent) || 0,
      total:
        serviceCharge * quantity +
        gstAmountFromPercent(serviceCharge, item.gstPercent) * quantity,
      hasDynamicSubService: false,
      hasDynamicPrice: false,
    };
  });

  const listTotal = selectedServices.reduce(
    (s, r) => s + (Number(r.total) || 0),
    0,
  );
  const payable = money(
    indent.finalAmountInr != null
      ? indent.finalAmountInr
      : Math.max(0, listTotal - couponOff),
  );
  const claimedCouponId =
    prefill.appliedCouponItems.find((c) => c.claimedCouponId)?.claimedCouponId ||
    null;

  return {
    customer_name: prefill.customer.name,
    customer_phone: prefill.customer.phone,
    profession: '',
    customer_profession: '',
    customer_email: null,
    customer_address: null,
    customer_city: '',
    customer_pincode: '',
    payment_method: ['cash', 'upi', 'card', 'split'].includes(
      String(indent.paymentMethod || indent.payment_method || '').toLowerCase(),
    )
      ? String(indent.paymentMethod || indent.payment_method).toLowerCase()
      : 'upi',
    payment_status: 'paid',
    work_status: 'completed',
    workStatus: 'completed',
    remarks: prefill.customer.notes,
    notes: prefill.customer.notes,
    paymentVerified: true,
    qrCodeShown: true,
    paymentRequestSent: !!indent.payRequestSentAt,
    selectedServices,
    items: selectedServices.map((item) => ({
      service_name: item.name,
      quantity: item.quantity,
      price: item.price,
      service_charge: item.serviceCharge,
      challana_amount: 0,
      gst_amount: item.gstAmount || 0,
      other_tax_amount: 0,
      total: item.total,
      ...(item.id ? { service_id: item.id } : {}),
    })),
    couponBillItems: prefill.appliedCouponItems.map((item) => ({
      name: item.name,
      quantity: item.quantity || 1,
      actualPrice: item.actualPrice,
      offerPrice: item.offerPrice,
      actualTotal: item.actualTotal,
      afterOfferTotal: item.afterOfferTotal,
      totalSavings: item.totalSavings,
      offerType: item.offerType || 'allotted_retrieve',
      offerId: null,
      offerItemId: null,
      claimedCouponId: item.claimedCouponId || null,
      code: item.code || '',
      service: item.service || '',
    })),
    generatedCoupons: [],
    claimedCouponId,
    cartDiscount: 0,
    paymentData: {
      methods: (() => {
        const method = String(
          indent.paymentMethod || indent.payment_method || 'upi',
        ).toLowerCase();
        if (method === 'cash') return { cash: payable, upi: 0, card: 0 };
        if (method === 'card') return { cash: 0, upi: 0, card: payable };
        const split = indent.paymentMethods || indent.payment_methods;
        if (split && typeof split === 'object') {
          return {
            cash: money(split.cash),
            upi: money(split.upi),
            card: money(split.card),
          };
        }
        return { cash: 0, upi: payable, card: 0 };
      })(),
      totalPaid: payable,
      change: 0,
      dueAmount: 0,
    },
  };
}

export default {
  shopIndentToCartBillPrefill,
  shopIndentToSmartBillPayload,
  couponSavingsFromIndent,
  stashShopIndentCartPrefill,
  takeShopIndentCartPrefill,
  clearShopIndentCartPrefill,
};
